import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { defineAdSlot } from '../../utils/ads-util.ts';
import { isDntEnabled } from '@utils/dnt.ts';
import {
  AdConfigContext,
  IAdConfigContext,
} from '@moment/context/AdContext.ts';
import { IGeneralSize } from '@pages/Root/pages/HomeRoot/interfaces.ts';
import {
  AdConfig,
  IAdTag,
} from '@pages/Root/services/api/ads/ad-model-definitions.ts';
import { IIabCategory } from '@pages/Root/services/api/lookBook/lookbook-model-definitions.ts';
import { useAds } from '@moment/context/AdsContext.tsx';
import { DEFAULT_BANNER_HEIGHT } from '@moment/shared-code/ad-utils.tsx';

export interface AdUnitProps {
  adUnitId: string;
  adType: 'top' | 'bot';
  size: IGeneralSize;
  responsiveSize: any[];
  containerClassName: string;
  adBlockClassName: string;
  contentId?: string;
  pageIndex?: number;
  iabCategoryList?: IIabCategory[];
}

export const AdUnit = (props: AdUnitProps) => {
  const [adLoaded, setAdLoaded] = useState(false);
  const [displayAd, setDisplayAd] = useState(true);
  const { hideAdSlotIds } = useContext<IAdConfigContext>(AdConfigContext);

  const { adConfigData } = useAds();
  const [isDoNotSell] = useState<boolean>(isDntEnabled());

  const extraTargeting = useMemo(() => {
    const targeting: Record<string, string | string[]> = {
      mp_contentid: props.contentId || '',
      page_number: `${props.pageIndex}`,
    };

    if (props.iabCategoryList && props.iabCategoryList?.length) {
      targeting['iab_category_codes'] = props.iabCategoryList?.map(
        (item) => item.code
      );
      targeting['iab_category_names'] = props.iabCategoryList?.map(
        (item) => item.label
      );
    }
    return targeting;
  }, [props.contentId, props.pageIndex, props.iabCategoryList]);


  const adSlotId = `ad_unit_${props.adUnitId}`;

  const validateAdTag = useCallback((adTag: IAdTag | null): IAdTag => {
    if (adTag?.unitPath && adTag?.unitPath !== '') {
      return adTag;
    }
    console.warn(
      `Invalid ad tag found: ${JSON.stringify(adTag)} in slot ${props.adUnitId}`
    );
    return { unitPath: '/180049092/ROS_OM_DTMOMENT_WVIEW' };
  }, []);

  const renderAd = useCallback((adConfig: AdConfig) => {
    if (adConfig && !isDoNotSell) {
      let adDataType;
      switch (props.adType) {
        case 'top':
          adDataType = validateAdTag(adConfig.topAd);
          break;
        case 'bot':
          adDataType = validateAdTag(adConfig.bottomAd);
          break;
        default:
          console.warn(`Invalid ad type '${props.adType}', defaulting to top`);
          adDataType = validateAdTag(adConfig.topAd);
      }
      console.log(
        `defining ad slot ${props.adUnitId} as ${adDataType?.unitPath}`
      );
      defineAdSlot(
        adDataType,
        props.adUnitId,
        props.size,
        props.responsiveSize,
        extraTargeting
      );
    } else {
      setDisplayAd(false);
    }
  }, []);

  useEffect(() => {
    if (adConfigData && !adLoaded) {
      renderAd(new AdConfig(adConfigData));
      setAdLoaded(true);
    }
  }, [adConfigData]);

  useEffect(() => {
    if (hideAdSlotIds?.includes(adSlotId)) {
      setDisplayAd(false);
    }
  }, [hideAdSlotIds]);

  return displayAd ? (
    <div className={props.containerClassName}>
      <div
        key="ad"
        className={props.adBlockClassName}
        style={{ minHeight: DEFAULT_BANNER_HEIGHT }}
        id={adSlotId}
        data-testid="test-ad-block"
      ></div>
    </div>
  ) : null;
};
